import {FEATURE_GATES_UNLIMITED, FeatureGate, MAX_DATA_HISTORY_DAYS_UPGRADE_LIMIT} from 'common/report/constants';
import {useEventListener} from 'common/util/component-helpers';
import {NAVIGATION_KEY_CODES} from 'common/util/dom';
import {defineAndMakeJsxFactory} from 'common/util/jsx';
import {BuilderPaneBase} from 'common/widgets/cohorts-query-builder/common-components/builder-pane-base/index';
import {DateRange} from 'common/widgets/date-range-picker/models/date-range';
import {DisplayContext} from 'common/widgets/date-range-picker/models/types';
import {DateRangeType} from 'common/widgets/cohorts-query-builder/models/enums/builder-enums';
import {currentProjectStore} from 'common/report/stores/project-details';
import {useStore} from 'common/util/component-helpers';

import template from './index.jade';
import css from './index.styl?inline';

class CohortsBuilderTime extends BuilderPaneBase {
  shouldUpdate(state) {
    return !!state.stagedCohort && !!state.stagedCohort.getStagedBehaviorDateRange(state.stagedClausePosition);
  }

  connectedCallback() {
    super.connectedCallback();
    useEventListener(this, window, `keydown`, (ev) => {
      if (ev.keyCode === NAVIGATION_KEY_CODES.ENTER) {
        this.updateValueAndCommitStagedDateRange();
      }
    });
    useStore(this, currentProjectStore).fetch();
  }

  get config() {
    return {
      ...super.config,
      useShadowDom: true,
      css,
      template,
      helpers: {
        ...super.config.helpers,
        getDateRangeAttrs: () => {
          const maxHistoryDays = this.getMaxDataHistoryDays();
          let featureGatingAttrs = {};
          if (maxHistoryDays !== FEATURE_GATES_UNLIMITED) {
            featureGatingAttrs = {
              'show-upsell': maxHistoryDays < MAX_DATA_HISTORY_DAYS_UPGRADE_LIMIT,
              'max-history-days': maxHistoryDays,
            };
          }

          const projectAsOfDate = this.getProjectAsOfDate();
          let projectAsOfDateAttrs = {};
          if (projectAsOfDate) {
            projectAsOfDateAttrs = {
              'show-max-date-past-context': true,
              'max-date': projectAsOfDate,
            };
          }

          return {
            'display-context': DisplayContext.InsightsTimePicker,
            'date-range': JSON.stringify(this.datePickerDateRange.getAttributes()),
            ...featureGatingAttrs,
            ...projectAsOfDateAttrs,
          };
        },
        changedDateRange: (ev) => {
          const {dateRange} = ev.detail;
          const dateRangeUpdates = {
            customRangeType: dateRange.type,
            ...(dateRange.from ? {fromDate: dateRange.from} : {}),
            ...(dateRange.to ? {toDate: dateRange.to} : {}),
            ...(dateRange.window ? {value: dateRange.window.value, unit: dateRange.window.unit} : {}),
          };
          this.updateStagedBehaviorDateRange(dateRangeUpdates);
        },
        handleDateRangePickerInsert: (vnode) => {
          requestAnimationFrame(() => {
            switch (this.stagedDateRange.customRangeType) {
              case DateRangeType.Relative:
                vnode.elm.selectRelativeInput();
                break;
              case DateRangeType.On:
              case DateRangeType.Since:
                vnode.elm.focusFromInput();
                break;
              default:
                break;
            }
          });
        },
        hasValidStagedDateRange: () => {
          const maxHistoryDays = this.getMaxDataHistoryDays();
          return (
            this.datePickerDateRange.isValid() &&
            (maxHistoryDays === FEATURE_GATES_UNLIMITED ||
              this.datePickerDateRange.isWithinMaxHistoryLimit(maxHistoryDays))
          );
        },
        clickedSelectDateRange: () => this.updateValueAndCommitStagedDateRange(),
        updateRenderedSize: () => this._updateRenderedSize(), // override throttling
      },
    };
  }

  commitStagedDateRange() {
    const {stagedCohort, stagedClausePosition} = this.state;
    const {customRangeType, fromDate, toDate, unit, value} = this.stagedDateRange;
    if (stagedCohort.hasValidStagedDateRange(stagedClausePosition)) {
      let range;
      switch (customRangeType) {
        case DateRangeType.Relative:
          range = {unit, value};
          break;
        case DateRangeType.Between:
          range = {from: fromDate, to: toDate};
          break;
        case DateRangeType.On:
          range = {from: fromDate, to: fromDate};
          break;
        case DateRangeType.Since:
          range = {from: fromDate};
          break;
        default:
          break;
      }

      this.dispatchBubbledEvent(`selected-custom-range`, range);
    } else {
      // select invalid inputs if clause is invalid
      switch (customRangeType) {
        case DateRangeType.Relative:
          this.dateRangePicker.selectRelativeInput();
          break;
        case DateRangeType.Between:
          this.dateRangePicker.selectToInput();
          break;
        default:
          this.dateRangePicker.selectFromInput();
          break;
      }
    }
  }

  updateValueAndCommitStagedDateRange() {
    // HACK: datetime inputs require a blur event to update their state and trigger a change event,
    // which then gets picked up by the model. this ensures that happens before we save changes
    this.focus();
    requestAnimationFrame(() => this.commitStagedDateRange());
  }

  updateStagedBehaviorDateRange(options) {
    const {stagedCohort, stagedClausePosition} = this.state;
    this.app.updateStagedCohort(stagedCohort.updateStagedBehaviorDateRange(options, stagedClausePosition));
  }

  get stagedDateRange() {
    const {stagedCohort, stagedClausePosition} = this.state;
    return stagedCohort.getStagedBehaviorDateRange(stagedClausePosition);
  }

  get datePickerDateRange() {
    const {customRangeType, fromDate, toDate, unit, value} = this.stagedDateRange;
    return new DateRange({
      type: customRangeType,
      from: fromDate,
      to: toDate,
      ...(fromDate
        ? {
            rolling: DateRange.isRelativeDate(fromDate),
          }
        : {}),
      window: {
        unit,
        value,
      },
    });
  }

  get dateRangePicker() {
    return this.el.querySelector(`date-range-picker`);
  }

  getMaxDataHistoryDays() {
    return this.app.context.getFeatureGateValue(FeatureGate.MaxDataHistoryDays);
  }

  getProjectAsOfDate() {
    return currentProjectStore.value?.projectAsOfDate || ``;
  }
}

export const CohortsBuilderTimeEl = defineAndMakeJsxFactory(`cohorts-builder-time`, CohortsBuilderTime);
